/**
 * Home geo + CTA resolution tracking for PostHog (project 422815).
 *
 * Every session gets HogQL-filterable props for:
 *   - country / province / city (Worker inject)
 *   - admin wishlist match (cta_resolved_from / matched)
 *   - whether the hero CTA is hidden (e.g. CABA = none)
 *   - which home surface painted (BaseTemplate vs DualCTA exclusive)
 *
 * Events:
 *   - `home_cta_resolved` — once per tab session on home mount
 * Super properties (every event after register):
 *   - site_country, ar_province, ar_city, cta_visible, cta_type, …
 */

import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
import {
  getBaseTemplatePosthogDistinctId,
  getBaseTemplateSessionId,
} from "@/lib/baseTemplateAnalytics";
import {
  getServerTemplateConfig,
  type ServerTemplateConfig,
} from "@/lib/serverTemplateDetection";
import { resolveWorkerDrivenCta } from "@/lib/workerDrivenCta";
import { isPostHogConfigured, posthog } from "@/lib/posthogAnalytics";

export type HomeCtaSurface =
  | "base_template"
  | "dual_cta_exclusive"
  | "dual_cta_intl"
  | "other";

export type HomeCtaResolvedInput = {
  /** SPA-effective template after resolveEffectiveHomeTemplate */
  effectiveTemplate?: string | null;
  /** True when AR Exclusive Mux home (showCommunity / dual_video) */
  arExclusiveMuxHome?: boolean;
  isArgentinaVisitor?: boolean;
  pathname?: string;
};

const SESSION_KEY = "__anaa_home_cta_resolved_v1";

function isCabaProvince(province: string | null | undefined): boolean {
  if (!province) return false;
  const n = province
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
  return (
    n === "caba" ||
    n === "ciudad autonoma de buenos aires" ||
    n === "capital federal" ||
    n === "ciudad de buenos aires"
  );
}

/**
 * Flatten Worker + SPA home decision into PostHog-friendly props.
 * Safe for super-properties (no PII).
 */
export function buildHomeCtaTrackingProps(
  input: HomeCtaResolvedInput = {},
): Record<string, string | number | boolean | null> {
  const cfg = getServerTemplateConfig();
  const workerCta = resolveWorkerDrivenCta();
  const pathname =
    input.pathname ||
    (typeof window !== "undefined" ? window.location.pathname : "/");

  const hideCta = cfg?.hideCta === true;
  const showCommunity = cfg?.showCommunity === true;
  const showBirthday = cfg?.showBirthday === true;
  const showDualVideo = cfg?.showDualVideo === true;
  const showWishlist = cfg?.showWishlist === true;

  let ctaTypeAdmin: string = "unknown";
  if (!cfg) {
    ctaTypeAdmin = "no_worker_config";
  } else if (hideCta) {
    ctaTypeAdmin = "none";
  } else if (showCommunity) {
    ctaTypeAdmin = "community";
  } else if (showDualVideo) {
    ctaTypeAdmin = "dual_video";
  } else if (showBirthday) {
    ctaTypeAdmin = "birthday";
  } else if (showWishlist) {
    ctaTypeAdmin = "wishlist";
  } else {
    ctaTypeAdmin = "none";
  }

  const heroCtaVisible = hideCta
    ? false
    : workerCta.mode === "hidden"
      ? false
      : workerCta.mode === "worker"
        ? true
        : showCommunity || showBirthday || showWishlist || showDualVideo;

  let heroCtaMode: string = "none";
  if (hideCta) heroCtaMode = "hidden_admin";
  else if (workerCta.mode === "worker") heroCtaMode = workerCta.variant;
  else if (workerCta.mode === "hidden") heroCtaMode = "hidden";
  else heroCtaMode = "default_or_unset";

  const effective =
    (input.effectiveTemplate || cfg?.templateName || "").trim() || "unknown";
  let homeSurface: HomeCtaSurface = "other";
  if (
    input.arExclusiveMuxHome ||
    effective === "DualCTAVideoLanding" ||
    effective === "InvitacionExclusivaMux"
  ) {
    homeSurface =
      input.isArgentinaVisitor || cfg?.country === "AR"
        ? "dual_cta_exclusive"
        : "dual_cta_intl";
  } else if (
    effective === "BaseTemplate" ||
    effective === "BaseTemplateBlack" ||
    effective === "ArgentinaBioTemplate"
  ) {
    homeSurface = "base_template";
  }

  const province = cfg?.province?.trim() || null;
  const city = cfg?.city?.trim() || null;
  const country = cfg?.country || null;
  const isCaba = isCabaProvince(province);

  const provinceDetectionMethod =
    (cfg as ServerTemplateConfig & { provinceDetectionMethod?: string })
      ?.provinceDetectionMethod ?? null;

  return {
    site_country: country,
    ar_province: province,
    ar_city: city,
    detection_method: cfg?.detectionMethod ?? null,
    province_detection_method: provinceDetectionMethod,
    cf_country: cfg?.cfCountry ?? null,
    cta_resolved_from: cfg?.ctaResolvedFrom ?? null,
    cta_resolved_matched: cfg?.ctaResolvedMatched ?? null,
    cta_type: ctaTypeAdmin,
    cta_type_admin: ctaTypeAdmin,
    cta_visible: heroCtaVisible,
    cta_hidden: !heroCtaVisible,
    hide_cta: hideCta,
    show_community: showCommunity,
    show_birthday: showBirthday,
    show_dual_video: showDualVideo,
    show_wishlist: showWishlist,
    hero_cta_mode: heroCtaMode,
    worker_cta_mode: workerCta.mode,
    worker_cta_variant:
      workerCta.mode === "worker" ? workerCta.variant : null,
    home_template: effective,
    home_surface: homeSurface,
    ar_exclusive_mux_home: Boolean(input.arExclusiveMuxHome),
    is_argentina_visitor: Boolean(
      input.isArgentinaVisitor || country === "AR",
    ),
    is_caba: isCaba,
    caba_cta_suppressed:
      isCaba && hideCta && !showCommunity && !heroCtaVisible,
    landing_path: pathname,
    page_path: pathname,
  };
}

/**
 * Register geo + CTA as PostHog super properties + person props.
 */
export function registerHomeCtaSuperProperties(
  input: HomeCtaResolvedInput = {},
): Record<string, string | number | boolean | null> {
  const props = buildHomeCtaTrackingProps(input);
  if (!isPostHogConfigured()) return props;

  try {
    posthog.register({
      site_country: props.site_country,
      ar_province: props.ar_province,
      ar_city: props.ar_city,
      cta_type: props.cta_type,
      cta_visible: props.cta_visible,
      cta_hidden: props.cta_hidden,
      hide_cta: props.hide_cta,
      show_community: props.show_community,
      cta_resolved_from: props.cta_resolved_from,
      cta_resolved_matched: props.cta_resolved_matched,
      home_template: props.home_template,
      home_surface: props.home_surface,
      is_caba: props.is_caba,
      caba_cta_suppressed: props.caba_cta_suppressed,
      hero_cta_mode: props.hero_cta_mode,
      detection_method: props.detection_method,
    });

    posthog.setPersonProperties(
      {
        last_site_country: props.site_country,
        last_ar_province: props.ar_province,
        last_ar_city: props.ar_city,
        last_cta_type: props.cta_type,
        last_cta_visible: props.cta_visible,
        last_home_template: props.home_template,
        last_is_caba: props.is_caba,
      },
      {
        first_site_country: props.site_country,
        first_ar_province: props.ar_province,
        first_cta_type: props.cta_type,
      },
    );
  } catch {
    /* noop */
  }
  return props;
}

/**
 * Fire `home_cta_resolved` once per tab session + refresh super properties.
 */
export function trackHomeCtaResolved(input: HomeCtaResolvedInput = {}): void {
  if (typeof window === "undefined") return;

  const props = registerHomeCtaSuperProperties(input);

  try {
    const w = window as unknown as Record<string, unknown>;
    if (w[SESSION_KEY]) return;
    w[SESSION_KEY] = true;
  } catch {
    /* continue */
  }

  mirrorFunnelEvent({
    event_name: "home_cta_resolved",
    posthog_distinct_id: getBaseTemplatePosthogDistinctId(),
    session_id: getBaseTemplateSessionId(),
    template: String(props.home_template || "home"),
    flow_name: "home_cta_resolution",
    landing_path: String(props.landing_path || "/"),
    country:
      typeof props.site_country === "string" ? props.site_country : undefined,
    props: {
      ...props,
      funnel_stage: "awareness",
      funnel_name: "home_cta_resolution",
    },
  });
}

/** Props to merge into DualCTA / membership / BaseTemplate funnel events. */
export function homeCtaPropsForFunnel(
  input: HomeCtaResolvedInput = {},
): Record<string, string | number | boolean | null> {
  return buildHomeCtaTrackingProps(input);
}
